/**
 * Take-over / act-on-behalf-of banner. Renders a persistent destructive bar
 * at the very top of the app shell when THIS TAB was entered via Customers'
 * "Access school/chef view" or "View as Parent". Makes it unmistakable that
 * the operator is acting as an organization or household in the live
 * production app, and offers a one-click exit.
 *
 * Mounted in `app-shell.tsx` above the top bar; renders nothing for ordinary
 * sessions (no impersonation context on `/api/auth/me`).
 */

import { Button } from '@ordo/ui/button'
import { AlertTriangle, Eye, LogOut } from 'lucide-react'
import { useState } from 'react'

import { apiFetch, clearImpersonationTokens } from '@/api/http'
import { type ImpersonationContext, useAuthStore } from '@/stores/auth'

export function ImpersonationBanner() {
  const impersonation = useAuthStore((s) => s.user?.impersonation ?? null)
  const [isExiting, setIsExiting] = useState(false)

  if (!impersonation) {
    return null
  }

  async function exit() {
    setIsExiting(true)
    try {
      // apiFetch sends the per-tab impersonation header so the server revokes
      // THIS tab's session; then we drop the tab's sessionStorage tokens so it
      // reverts to the operator's own session.
      const body = await apiFetch<{ redirectTo?: string | null }>('/api/auth/impersonate/exit', { method: 'POST' })
      clearImpersonationTokens()
      window.location.href = body?.redirectTo || '/'
    } catch {
      clearImpersonationTokens()
      setIsExiting(false)
    }
  }

  return (
    <div className="flex w-full items-center justify-between gap-3 border-b border-amber-400 bg-amber-300 px-4 py-2 text-amber-950">
      <div className="flex min-w-0 items-center gap-2">
        {impersonation.readOnly ? (
          <Eye className="h-4 w-4 shrink-0" aria-hidden />
        ) : (
          <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden />
        )}
        <p className="truncate text-sm">
          {impersonation.readOnly ? 'Viewing' : 'Accessing'} the app as <strong>{impersonation.orgName}</strong>.
          <span className="ml-2 hidden sm:inline">{bannerDetail(impersonation)}</span>
        </p>
      </div>
      <Button
        variant="outline"
        size="sm"
        onClick={() => void exit()}
        disabled={isExiting}
        className="h-7 shrink-0 gap-1 border-amber-900/30 bg-amber-950/10 text-amber-950 hover:bg-amber-950/20 hover:text-amber-950">
        <LogOut className="h-3 w-3" aria-hidden />
        {isExiting ? 'Exiting…' : 'Exit view'}
      </Button>
    </div>
  )
}

/**
 * The banner's second sentence. A guest's view-only visit (`readOnly`) says so
 * instead of warning that changes are real: the server refuses every change.
 */
export function bannerDetail(impersonation: Pick<ImpersonationContext, 'kind' | 'readOnly'>): string {
  if (impersonation.readOnly) {
    return 'View only — this is real data, and changes are turned off.'
  }
  return impersonation.kind === 'household'
    ? 'This is the live production app — orders you place are real and charge the household.'
    : 'This is the live production app — changes you make are real and affect the organization.'
}
